import {
  binaryMetric,
  currencyMetric,
  currencyMetricQuery,
  durationMetric,
  filter,
  literal,
  metric,
  percentMetric,
  toCurrency,
  toPercent,
  valueSet,
} from 'utils/metrics/constructors';
import protos from 'utils/protos';

const { PartyState, TicketType } = protos;
const { Variety } = TicketType;

const GrossServicedPayments = currencyMetric(
  'Gross income',
  'NIGHT_OF_PAYMENT_PROCESSED_LOCAL_CENTS',
  'Gross income towards the day of service.'
);

const NightOfBookings = metric(
  'Reservations',
  'NIGHT_OF_BOOKINGS_COUNT',
  'Number of reservations on the day of service that are not cancelled.'
);

const NightOfDineInPayments = metric(
  'Dine-in sales',
  'NIGHT_OF_NET_AMOUNT_PROCESSED_LOCAL_CENTS',
  'Net sales from dine-in reservations towards the day of service.',
  'currency',
  toCurrency,
  filter({
    operation: 'AND',
    valueSet: [
      valueSet('EXPERIENCE_VARIETY', [
        Variety.DEPOSIT,
        Variety.FULL,
        Variety.RESERVATION,
        Variety.DEPRECATED_SPECIAL_TABLE,
      ]),
    ],
  })
);

const NightOfEventPayments = metric(
  'Event sales',
  'NIGHT_OF_NET_AMOUNT_PROCESSED_LOCAL_CENTS',
  'Net sales from event reservations towards the day of service.',
  'currency',
  toCurrency,
  filter({
    operation: 'AND',
    valueSet: [
      valueSet('EXPERIENCE_VARIETY', [
        Variety.DEPRECATED_EVENT,
        Variety.SEATED_EVENT,
        Variety.GA_EVENT,
      ]),
    ],
  })
);

const NightOfPickupPayments = metric(
  'Pickup sales',
  'NIGHT_OF_NET_AMOUNT_PROCESSED_LOCAL_CENTS',
  'Net sales from pickup orders towards the day of service.',
  'currency',
  toCurrency,
  filter({
    operation: 'AND',
    valueSet: [valueSet('EXPERIENCE_VARIETY', [Variety.ORDER_PICKUP])],
  })
);

const NightOfDeliveryPayments = metric(
  'Delivery sales',
  'NIGHT_OF_NET_AMOUNT_PROCESSED_LOCAL_CENTS',
  'Net sales from delivery orders towards the day of service.',
  'currency',
  toCurrency,
  filter({
    operation: 'AND',
    valueSet: [valueSet('EXPERIENCE_VARIETY', [Variety.ORDER_DELIVERY])],
  })
);

const NightOfDineInBookings = metric(
  'Dine-in reservations',
  'NIGHT_OF_BOOKINGS_COUNT',
  'Number of dine-in reservations on the day of service that are not cancelled.',
  'number',
  null,
  filter({
    operation: 'AND',
    valueSet: [
      valueSet('EXPERIENCE_VARIETY', [
        Variety.DEPOSIT,
        Variety.FULL,
        Variety.RESERVATION,
        Variety.DEPRECATED_SPECIAL_TABLE,
      ]),
    ],
  })
);

const NightOfEventBookings = metric(
  'Event reservations',
  'NIGHT_OF_BOOKINGS_COUNT',
  'Number of event reservation on the day of service that are not cancelled.',
  'number',
  null,
  filter({
    operation: 'AND',
    valueSet: [
      valueSet('EXPERIENCE_VARIETY', [
        Variety.DEPRECATED_EVENT,
        Variety.SEATED_EVENT,
        Variety.GA_EVENT,
      ]),
    ],
  })
);

const NightOfPickupBookings = metric(
  'Pickup orders',
  'NIGHT_OF_BOOKINGS_COUNT',
  'Number of pickup orders on the day of service that are not cancelled.',
  'number',
  null,
  filter({
    operation: 'AND',
    valueSet: [valueSet('EXPERIENCE_VARIETY', [Variety.ORDER_PICKUP])],
  })
);

const NightOfDeliveryBookings = metric(
  'Delivery orders',
  'NIGHT_OF_BOOKINGS_COUNT',
  'Number of delivery orders on the day of service that are not cancelled.',
  'number',
  null,
  filter({
    operation: 'AND',
    valueSet: [valueSet('EXPERIENCE_VARIETY', [Variety.ORDER_DELIVERY])],
  })
);

const NightOfToGoBookings = metric(
  'Orders',
  'NIGHT_OF_BOOKINGS_COUNT',
  'Number of delivery or pickup orders on the day of service that are not cancelled.',
  'number',
  null,
  filter({
    operation: 'AND',
    valueSet: [valueSet('EXPERIENCE_VARIETY', [Variety.ORDER_DELIVERY, Variety.ORDER_PICKUP])],
  })
);

const NightOfDeferredPayment = currencyMetric(
  'Pay later',
  'NIGHT_OF_DEFERRED_PAYMENT_LOCAL_CENTS',
  'Amount of pay later'
);
const NightOfGiftCards = currencyMetric(
  'Sales',
  'NIGHT_OF_GIFT_CERTIFICATES_LOCAL_CENTS',
  'The total value of gift cards redeemed.'
);
const NightOfPromoCodeDiscounts = currencyMetric(
  'Sales',
  'NIGHT_OF_PROMO_CODE_DISCOUNT_LOCAL_CENTS',
  'The total value of discounts.'
);
const NightOfGoodsSoldAdjustments = currencyMetric(
  'Net adjustments',
  'NIGHT_OF_GOODS_SOLD_ADJUSTMENTS_LOCAL_CENTS',
  'The sum of tax and service less comp and discount, including un-paid purchases'
);
const NightOfGoodsSoldComp = currencyMetric(
  'Comps',
  'NIGHT_OF_GOODS_SOLD_COMP_LOCAL_CENTS',
  'Total value of comp, including un-paid purchases'
);
const NightOfGoodsSoldDiscount = currencyMetric(
  'Discounts',
  'NIGHT_OF_GOODS_SOLD_DISCOUNT_LOCAL_CENTS',
  "Discounts applied to a reservation's amount owed"
);
const NightOfGoodsSoldLoyaltyProgramDiscount = currencyMetric(
  'Reward Dollars',
  'NIGHT_OF_LOYALTY_PROGRAM_DISCOUNT_LOCAL_CENTS',
  'Discounts applied by redeeming loyalty program Reward Dollars'
);
const NightOfGoodsSoldGratuity = currencyMetric(
  'Gratuity',
  'NIGHT_OF_GOODS_SOLD_GRATUITY_LOCAL_CENTS',
  'Sum of gratuity charges collected, including unpaid purchases'
);
const NightOfGoodsSoldService = currencyMetric(
  'Service',
  'NIGHT_OF_GOODS_SOLD_SERVICE_LOCAL_CENTS',
  'Sum of service charge collected, including unpaid purchases'
);
const NightOfGoodsSoldSubtotal = currencyMetric(
  'Gross reservation value',
  'NIGHT_OF_GOODS_SOLD_SUBTOTAL_LOCAL_CENTS',
  'Gross value of items reserved, including un-paid purchases'
);
const NightOfGoodsSoldEventFee = currencyMetric(
  'Event fee',
  'NIGHT_OF_GOODS_SOLD_EVENT_FEE_LOCAL_CENTS',
  'Sum of event fees collected, including un-paid purchases'
);
const NightOfGoodsSoldCustomFee = currencyMetric(
  'Sales',
  'NIGHT_OF_GOODS_SOLD_CUSTOM_FEE_LOCAL_CENTS',
  'Sum of custom fees collected, including un-paid purchases'
);
const NightOfGoodsSoldTax = currencyMetric(
  'Tax',
  'NIGHT_OF_GOODS_SOLD_TAX_LOCAL_CENTS',
  'Sum of taxes collected, including unpaid purchases'
);
const NightOfGoodsSoldTotal = currencyMetric(
  'Net total sales',
  'NIGHT_OF_GOODS_SOLD_TOTAL_LOCAL_CENTS',
  'Total value of items sold, including un-paid purchases'
);
const NightOfPurchasedCharges = currencyMetric(
  'Sales',
  'NIGHT_OF_PURCHASED_CHARGE_LOCAL_CENTS',
  'Gross value of custom charges sold, including un-paid purchases'
);
const NightOfPurchasedExperiences = currencyMetric(
  'Sales',
  'NIGHT_OF_PURCHASED_EXPERIENCE_LOCAL_CENTS',
  'Gross value of experiences sold, including un-paid purchases'
);
const NightOfDepositsAppliedTableside = currencyMetric(
  'Deposits applied tableside',
  'NIGHT_OF_DEPOSITS_APPLIED_TABLESIDE_LOCAL_CENTS',
  'Total value of deposits applied tableside'
);
const NightOfBusinessPayablePurchasedFees = currencyMetric(
  'Sales',
  'NIGHT_OF_BUSINESS_PAYABLE_PURCHASED_FEE_LOCAL_CENTS',
  'Gross value of fees sold, including un-paid purchases'
);
const NightOfPurchasedSupplements = currencyMetric(
  'Sales',
  'NIGHT_OF_PURCHASED_SUPPLEMENT_LOCAL_CENTS',
  'Gross value of add-ons sold, including un-paid purchases'
);
const NightOfSellthrough = percentMetric(
  'Sell-through',
  'NIGHT_OF_COVERS_SELLTHROUGH_PERCENT_CENTS',
  'Sell-through is computed as the percent of sold covers over bookable covers (unheld covers) on the day of service.'
);
const NightOfSoldCovers = metric(
  'Reserved covers',
  'NIGHT_OF_COVERS_BOUGHT',
  'Number of covers reserved for the day of service that are not cancelled.'
);
const NightOfOfferedCovers = metric(
  'Offered covers',
  'NIGHT_OF_COVERS_COUNT',
  'Total number of offered covers on the day of service.'
);
const NightOfHeldCovers = metric(
  'Held covers',
  'NIGHT_OF_HELD_COVERS',
  'Total number of held covers on the day of service.'
);
const NightOfSoldSupplements = metric(
  'Reserved covers',
  'NIGHT_OF_PURCHASED_SUPPLEMENT_COUNT',
  'Count of items sold on the day of service.'
);
const NightOfSoldFees = metric(
  'Reserved covers',
  'NIGHT_OF_PURCHASED_FEE_COUNT',
  'Count of items sold on the day of service.'
);
const NightOfSoldCharges = metric(
  'Reserved covers',
  'NIGHT_OF_PURCHASED_CUSTOM_CHARGE_COUNT',
  'Count of items sold on the day of service.'
);
const NightOfRetainedCancelledPayment = currencyMetric(
  'Retained payment from cancellations',
  'NIGHT_OF_RETAINED_PAYMENTS_FROM_CANCELLED',
  'Amount of payment received for reservations that were eventually cancelled'
);
const NightOfPosChecks = metric('Checks', 'NIGHT_OF_POS_CHECK_COUNT', 'Number of checks');
const NightOfPosCovers = metric('Covers', 'NIGHT_OF_POS_COVER_COUNT', 'Number of covers');
const NightOfOnlineSales = currencyMetric(
  'Tock sales',
  'NIGHT_OF_POS_ONLINE_SALES_TOTAL_LOCAL_CENTS',
  'Sales from Tock'
);
const NightOfInStoreSales = currencyMetric(
  'POS sales',
  'NIGHT_OF_POS_INSTORE_SALES_TOTAL_LOCAL_CENTS',
  'Sales from the point of sale'
);
const NightOfTotalSales = currencyMetric(
  'Total sales',
  'NIGHT_OF_POS_SALES_TOTAL_LOCAL_CENTS',
  'Total sales from Tock and point of sale'
);
const NightOfPosTockPaymentTotal = currencyMetric(
  'Tock payments',
  'NIGHT_OF_POS_TOCK_PAYMENT_TOTAL_LOCAL_CENTS',
  'Payments from Tock'
);
const NightOfPosPaymentTotal = currencyMetric(
  'POS payments',
  'NIGHT_OF_POS_PAYMENT_TOTAL_LOCAL_CENTS',
  'Sales from the on-premise point of sale'
);
const NightOfPosTurnTime = durationMetric(
  'Turn time sec',
  'NIGHT_OF_POS_TURN_TIME_SEC',
  'Turn time calculated from point of sale check open and close time'
);
const NightOfPosCheckAverage = currencyMetric(
  'Check average',
  'NIGHT_OF_POS_CHECK_AVERAGE_LOCAL_CENTS',
  'Check average'
);
const NightOfPosPersonAverage = currencyMetric(
  'Guest average',
  'NIGHT_OF_POS_SALES_PER_COVER_LOCAL_CENTS',
  'Person average'
);
const NightOfPosAvgTurnTime = durationMetric(
  'Avg turn time',
  'NIGHT_OF_POS_AVG_TURN_TIME_SEC',
  'Average turn time calculated from point of sale check open and close time'
);

const NightOfTurnTime = durationMetric(
  'Turn time sec',
  'NIGHT_OF_TURN_TIME_SEC',
  'Sum of turn time'
);
const NightOfAvgTurnTime = durationMetric(
  'Avg turn time',
  'NIGHT_OF_AVG_TURN_TIME_SEC',
  'Average turn time'
);

const RefundsServiced = currencyMetric(
  'Refunds',
  'NIGHT_OF_REFUND_ISSUED_LOCAL_CENTS',
  'Refunds on reservations towards the day of service.'
);
const ServicedPayments = currencyMetric(
  'Net income',
  'NIGHT_OF_NET_AMOUNT_PROCESSED_LOCAL_CENTS',
  'Net realized sales towards the day of service which is the difference between all payments and refunds towards the day.'
);

const CumulativeBalance = currencyMetricQuery(
  'Unrealized income',
  {
    // instant 0 to last date in range purchases based on creation date
    operation: 'CUMULATIVE',
    unary: {
      operation: 'MINUS',
      unary: {
        value: 'BOOKING_NET_AMOUNT_PROCESSED_LOCAL_CENTS',
        // all $ processed
        // - (delivery fees for all $ processed taken out)
        // = - (delivery fees for $ processed on or before tonight) - (delivery fees for $ processed after tonight)
      },
      binary: {
        value: 'NIGHT_OF_NET_AMOUNT_PROCESSED_LOCAL_CENTS',
        // $ on or before end of date range
        // - delivery fees for $ processed on or before tonight
      },
    },
  },
  'Unrealized income, that is the sum total of revenue from future reservations'
);

// Composed metrics
const NightOfGoodsSoldTotalPlusRetained = binaryMetric(
  'Total sold reservation value',
  'PLUS',
  NightOfGoodsSoldTotal,
  NightOfRetainedCancelledPayment,
  'The sum of total goods sold plus retained payment from cancelled reservations',
  'currency',
  toCurrency
);

const NightOfIncomePlusGiftCards = binaryMetric(
  'Net income + GCs',
  'PLUS',
  NightOfGiftCards,
  ServicedPayments,
  'Total expected payments plus payment via gift cards',
  'currency',
  toCurrency
);

const NightOfRealizedRevenue = binaryMetric(
  'Realized revenue',
  'MINUS',
  binaryMetric('', 'PLUS', NightOfGoodsSoldTotal, NightOfRetainedCancelledPayment),
  NightOfDeferredPayment,
  'The amount of revenue that is realized on the date(s) selected',
  'currency',
  toCurrency
);

const NightOfUnsoldCovers = binaryMetric(
  'Available covers',
  'MINUS',
  NightOfOfferedCovers,
  binaryMetric('', 'PLUS', NightOfSoldCovers, NightOfHeldCovers),
  'Total number of unsold covers on the day of service.'
);

const NightOfSalesPerSeatHour = currencyMetric(
  'Sales per seat-hour',
  'NIGHT_OF_POS_SALES_PER_SEAT_HOUR',
  'Sales per seat per hour.'
);

const NightOfCancelledBookings = metric(
  'Cancelled reservations',
  'NIGHT_OF_GROSS_BOOKINGS_COUNT',
  'Number of cancelled reservations. This do not count towards total reservations for the night',
  'number',
  null,
  filter({ operation: 'AND', valueSet: [valueSet('IS_CANCELLED', [1])] })
);

const NightOfCancelledCovers = metric(
  'Cancelled covers',
  'NIGHT_OF_GROSS_COVERS_BOUGHT',
  'Number of cancelled covers. This do not count towards the total reserved covers for the night',
  'number',
  null,
  filter({ operation: 'AND', valueSet: [valueSet('IS_CANCELLED', [1])] })
);

const NightOfNoShowBookings = metric(
  'No-show reservations',
  'NIGHT_OF_BOOKINGS_COUNT',
  'Number of reservations that no-showed out of the total reservations for the night',
  'number',
  null,
  filter({ operation: 'AND', valueSet: [valueSet('PARTY_STATUS_ID', [PartyState.NO_SHOW])] })
);

const NightOfNoShowCovers = metric(
  'No-show covers',
  'NIGHT_OF_COVERS_BOUGHT',
  'Number of reserved guests who no-showed out of the total reservations for the night',
  'number',
  null,
  filter({ operation: 'AND', valueSet: [valueSet('PARTY_STATUS_ID', [PartyState.NO_SHOW])] })
);

const validWalkinFilter = filter({
  operation: 'AND',
  filter: [
    filter({
      operation: 'NOT',
      valueSet: [
        valueSet('PARTY_STATUS_ID', [
          PartyState.EXPECTED,
          PartyState.ARRIVED,
          PartyState.NO_SHOW,
          PartyState.CANCELED,
        ]),
      ],
    }),
  ],
  valueSet: [valueSet('IS_WALKIN', [1])],
});

const NightOfWalkIns = metric(
  'Walk-ins',
  'VISIT_TOTAL_VISITS',
  'Number of walk-in visits',
  'number',
  null,
  validWalkinFilter
);

const NightOfWalkInCovers = metric(
  'Walk-in covers',
  'VISIT_COVERS',
  'Number of covers that walked-in',
  'number',
  null,
  validWalkinFilter
);

const NoShowPercent = binaryMetric(
  'No-show percent',
  'QUOT',
  binaryMetric('', 'MULT', NightOfNoShowCovers, literal(10000)),
  NightOfSoldCovers,
  'Percent of covers marked as no-show',
  'percentCents',
  toPercent
);

const NightOfTotalParties = binaryMetric(
  'Reservations + Walk-ins',
  'PLUS',
  NightOfWalkIns,
  NightOfBookings,
  'Total reservation and walk-in parties for the night',
  'number'
);

const NightOfTotalCovers = binaryMetric(
  'Reservations + Walk-ins Covers',
  'PLUS',
  NightOfWalkInCovers,
  NightOfSoldCovers,
  'Total reservation and walk-in covers for the night',
  'number'
);

const NightOfTotalCoversWithoutNoShows = binaryMetric(
  'Covers + Walkins - No-shows',
  'MINUS',
  NightOfTotalCovers,
  NightOfNoShowCovers,
  'Total covers for the night excluding no-shows and cancellations',
  'number'
);

const NightOfTotalBookingsWithoutNoShows = binaryMetric(
  'Reservations',
  'MINUS',
  NightOfBookings,
  NightOfNoShowBookings,
  'Total reservations for the night excluding no-shows and cancellations',
  'number'
);

export default {
  CumulativeBalance,
  GrossServicedPayments,
  NightOfDineInPayments,
  NightOfEventPayments,
  NightOfPickupPayments,
  NightOfDeliveryPayments,
  NightOfBookings,
  NightOfDineInBookings,
  NightOfEventBookings,
  NightOfPickupBookings,
  NightOfDeliveryBookings,
  NightOfToGoBookings,
  NightOfDeferredPayment,
  NightOfDepositsAppliedTableside,
  NightOfGiftCards,
  NightOfPromoCodeDiscounts,
  NightOfGoodsSoldAdjustments,
  NightOfGoodsSoldComp,
  NightOfGoodsSoldDiscount,
  NightOfGoodsSoldLoyaltyProgramDiscount,
  NightOfGoodsSoldEventFee,
  NightOfGoodsSoldCustomFee,
  NightOfGoodsSoldGratuity,
  NightOfGoodsSoldService,
  NightOfGoodsSoldSubtotal,
  NightOfGoodsSoldTax,
  NightOfGoodsSoldTotal,
  NightOfGoodsSoldTotalPlusRetained,
  NightOfHeldCovers,
  NightOfIncomePlusGiftCards,
  NightOfOfferedCovers,
  NightOfPurchasedCharges,
  NightOfPurchasedExperiences,
  NightOfBusinessPayablePurchasedFees,
  NightOfPurchasedSupplements,
  NightOfRealizedRevenue,
  NightOfRetainedCancelledPayment,
  NightOfSellthrough,
  NightOfSoldCovers,
  NightOfUnsoldCovers,
  NightOfSoldSupplements,
  NightOfSoldFees,
  NightOfSoldCharges,
  NightOfCancelledBookings,
  NightOfCancelledCovers,
  NightOfNoShowBookings,
  NightOfNoShowCovers,
  NightOfOnlineSales,
  NightOfInStoreSales,
  NightOfTotalSales,
  NightOfAvgTurnTime,
  NightOfTurnTime,
  NightOfPosTockPaymentTotal,
  NightOfPosPaymentTotal,
  NightOfPosChecks,
  NightOfPosCovers,
  NightOfPosAvgTurnTime,
  NightOfPosTurnTime,
  NightOfPosCheckAverage,
  NightOfPosPersonAverage,
  NightOfSalesPerSeatHour,
  NightOfWalkIns,
  NightOfWalkInCovers,
  NoShowPercent,
  RefundsServiced,
  ServicedPayments,
  NightOfTotalParties,
  NightOfTotalCovers,
  NightOfTotalBookingsWithoutNoShows,
  NightOfTotalCoversWithoutNoShows,
};
